/**
 * User Service
 * Handles all user-related API operations (CRUD)
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet, apiPost, apiPut, apiDelete, ApiError } from '../utils/apiClient';
import type {
  User,
  UserCreateRequest,
  UserListResponse,
  UserResponse,
  UserPaginationInfo,
} from '../types/user.types';

export interface UserServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: UserPaginationInfo;
}

function isUserListResponse(data: unknown): data is UserListResponse {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  if (!('success' in data) || !('data' in data)) {
    return false;
  }
  const candidate = data as { data: unknown };
  return Array.isArray(candidate.data);
}

function isUserResponse(data: unknown): data is UserResponse {
  return typeof data === 'object' && data !== null && 'success' in data && 'data' in data;
}

function isUserRecord(data: unknown): data is User {
  return typeof data === 'object' && data !== null && 'id' in data;
}

class UserService {
  private appendUserListFilterParams(
    queryParams: string[],
    filters?: {
      search?: string;
      name?: string;
      role?: string;
      employee_id?: string;
      phone_number?: string;
      status?: string;
    }
  ): void {
    if (!filters) {
      return;
    }
    if (filters.search?.trim()) {
      queryParams.push(`search=${encodeURIComponent(filters.search.trim())}`);
    }
    if (filters.name) {
      queryParams.push(`name=${encodeURIComponent(filters.name)}`);
    }
    if (filters.role) {
      queryParams.push(`role=${encodeURIComponent(filters.role)}`);
    }
    if (filters.employee_id) {
      queryParams.push(`doctor_id=${encodeURIComponent(filters.employee_id)}`);
    }
    if (filters.phone_number) {
      queryParams.push(`phone_number=${encodeURIComponent(filters.phone_number)}`);
    }
    if (filters.status) {
      queryParams.push(`status=${encodeURIComponent(filters.status)}`);
    }
  }

  private parseUserListResponse(responseData: unknown): UserServiceResponse<User[]> {
    if (!responseData || typeof responseData !== 'object') {
      return {
        success: false,
        error: 'Invalid response format',
        data: [],
      };
    }

    if (isUserListResponse(responseData)) {
      const apiResponse = responseData;
      if (apiResponse.success && apiResponse.data) {
        return {
          success: true,
          data: apiResponse.data,
          message: apiResponse.message,
          pagination: apiResponse.pagination,
        };
      }
      return {
        success: false,
        error: apiResponse.message || 'Failed to fetch users',
        data: [],
      };
    }

    if (Array.isArray(responseData)) {
      return {
        success: true,
        data: responseData,
      };
    }

    return {
      success: false,
      error: 'Invalid response format',
      data: [],
    };
  }

  /**
   * Get all users with pagination and filters
   * Uses GET /ris/api/users
   * @param page Optional page number (default: 1)
   * @param facilityId Optional facility_id to filter users by facility
   * @param filters Optional filter parameters (search, name, role, employee_id, phone_number, status)
   */
  async getAllUsers(
    page: number = 1,
    facilityId?: string | number,
    filters?: {
      /** Single search box: backend matches name, email, phone, employee/doctor id */
      search?: string;
      name?: string;
      role?: string;
      employee_id?: string;
      phone_number?: string;
      status?: string;
    }
  ): Promise<UserServiceResponse<User[]>> {
    try {
      const queryParams: string[] = [];
      if (page) {
        queryParams.push(`page=${page}`);
      }
      if (facilityId !== undefined && facilityId !== null && facilityId !== '') {
        queryParams.push(`facility_id=${encodeURIComponent(String(facilityId))}`);
      }
      this.appendUserListFilterParams(queryParams, filters);

      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      const url = `${API_ENDPOINTS.USERS.GET}${queryString}`;
      const response = await apiGet<UserListResponse | User[]>(url);

      return this.parseUserListResponse(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
        data: [],
      };
    }
  }

  /**
   * Get a single user by ID
   * Uses GET /ris/api/users/{id}
   */
  async getUserById(id: number | string): Promise<UserServiceResponse<User>> {
    try {
      const response = await apiGet<UserResponse | User>(API_ENDPOINTS.USERS.GET_BY_ID(id));

      // Handle different response formats
      let user: User;
      if (response.data && typeof response.data === 'object') {
        if (isUserResponse(response.data)) {
          const apiResponse = response.data;
          if (apiResponse.success && apiResponse.data) {
            user = apiResponse.data;
          } else {
            return {
              success: false,
              error: apiResponse.message || 'Failed to fetch user',
            };
          }
        } else if (isUserRecord(response.data)) {
          user = response.data;
        } else {
          return {
            success: false,
            error: 'Invalid response format',
          };
        }
      } else {
        return {
          success: false,
          error: 'Invalid response format',
        };
      }

      return {
        success: true,
        data: user,
      };
    } catch (error) {
      console.error('Error fetching user:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  /**
   * Create a new user
   * Uses POST /ris/api/auth/register
   * Accepts FormData for file uploads or UserCreateRequest for JSON
   */
  async createUser(userData: FormData | UserCreateRequest): Promise<UserServiceResponse<User>> {
    try {
      const response = await apiPost<UserResponse | User>(API_ENDPOINTS.USERS.CREATE, userData);

      // Check if response indicates failure
      if (response.data && typeof response.data === 'object') {
        const apiResponse = response.data as {
          success?: boolean;
          message?: string;
          data?: User;
          error?: string | { errorMessage?: string };
        };
        if (apiResponse.success === false) {
          // Handle error response - pass full response data to extractErrorMessage
          const errorMessage = this.extractErrorMessage({
            response: apiResponse,
            message: apiResponse.message || 'Failed to create user',
          } as ApiError);
          return {
            success: false,
            error: errorMessage,
          };
        }

        let user: User;
        if (isUserResponse(apiResponse) && apiResponse.data) {
          user = apiResponse.data;
        } else if (isUserRecord(response.data)) {
          user = response.data;
        } else {
          return {
            success: false,
            error: 'Invalid response format',
          };
        }

        return {
          success: true,
          data: user,
          message: 'User created successfully',
        };
      }

      return {
        success: false,
        error: 'Invalid response format',
      };
    } catch (error) {
      console.error('Error creating user:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  /**
   * Update an existing user
   * Uses PUT /ris/api/users/{id}
   * Accepts FormData for file uploads or Partial<UserCreateRequest> for JSON
   */
  async updateUser(
    id: number | string,
    userData: FormData | Partial<UserCreateRequest>
  ): Promise<UserServiceResponse<User>> {
    try {
      const response = await apiPut<UserResponse | User>(API_ENDPOINTS.USERS.UPDATE(id), userData);

      // Check if response indicates failure
      if (response.data && typeof response.data === 'object') {
        const apiResponse = response.data as {
          success?: boolean;
          message?: string;
          data?: User;
          error?: string | { errorMessage?: string };
        };
        if (apiResponse.success === false) {
          // Handle error response - pass full response data to extractErrorMessage
          const errorMessage = this.extractErrorMessage({
            response: apiResponse,
            message: apiResponse.message || 'Failed to update user',
          } as ApiError);
          return {
            success: false,
            error: errorMessage,
          };
        }

        let user: User;
        if (isUserResponse(apiResponse) && apiResponse.data) {
          user = apiResponse.data;
        } else if (isUserRecord(response.data)) {
          user = response.data;
        } else {
          return {
            success: false,
            error: 'Invalid response format',
          };
        }

        return {
          success: true,
          data: user,
          message: 'User updated successfully',
        };
      }

      return {
        success: false,
        error: 'Invalid response format',
      };
    } catch (error) {
      console.error('Error updating user:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  /**
   * Delete a user
   * Uses DELETE /ris/api/users/{id}
   */
  async deleteUser(id: number | string): Promise<UserServiceResponse<void>> {
    try {
      await apiDelete<void>(API_ENDPOINTS.USERS.DELETE(id));

      return {
        success: true,
        message: 'User deleted successfully',
      };
    } catch (error) {
      console.error('Error deleting user:', error);
      return {
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  }

  private getUserFieldValue(
    user: User,
    field: 'name' | 'role' | 'employee_id' | 'phone_number'
  ): string | undefined {
    switch (field) {
      case 'name':
        return user.full_name;
      case 'role':
        return user.role;
      case 'employee_id':
        return user.role === 'Radiologist' ? user.doctor_id : user.employee_id;
      case 'phone_number':
        return user.mobile_number;
      default:
        return undefined;
    }
  }

  private parseUsersFromResponse(responseData: unknown): User[] {
    if (!responseData || typeof responseData !== 'object') {
      return [];
    }
    if (isUserListResponse(responseData)) {
      const apiResponse = responseData;
      if (apiResponse.success && apiResponse.data) {
        return apiResponse.data;
      }
      return [];
    }
    if (Array.isArray(responseData)) {
      return responseData;
    }
    return [];
  }

  /**
   * Search users for filter dropdown options
   * Uses GET /ris/api/users with search parameter for fuzzy matching
   * @param field - The field to search (name, role, employee_id, phone_number)
   * @param searchQuery - The search query string
   * @param facilityId - Optional facility_id to filter users by facility
   * @returns Array of unique option values for the specified field
   */
  async searchUserOptions(
    field: 'name' | 'role' | 'employee_id' | 'phone_number',
    searchQuery: string,
    facilityId?: string | number
  ): Promise<{ value: string; label: string }[]> {
    try {
      const queryParams: string[] = [];
      queryParams.push('page=1');

      if (facilityId !== undefined && facilityId !== null && facilityId !== '') {
        queryParams.push(`facility_id=${encodeURIComponent(String(facilityId))}`);
      }

      if (searchQuery.trim()) {
        const backendField = field === 'employee_id' ? 'doctor_id' : field;
        queryParams.push(`${backendField}=${encodeURIComponent(searchQuery)}`);
      }

      const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
      const url = `${API_ENDPOINTS.USERS.GET}${queryString}`;
      const response = await apiGet<UserListResponse | User[]>(url);
      const users = this.parseUsersFromResponse(response.data);

      const uniqueValues = new Set<string>();
      const options: { value: string; label: string }[] = [];

      users.forEach(user => {
        const fieldValue = this.getUserFieldValue(user, field);
        if (fieldValue && !uniqueValues.has(fieldValue)) {
          uniqueValues.add(fieldValue);
          options.push({ value: fieldValue, label: fieldValue });
        }
      });

      return options;
    } catch (error) {
      console.error(`Error searching user options for ${field}:`, error);
      return [];
    }
  }

  /**
   * Get status options (Active/Inactive)
   * This is a static list, but kept here for consistency
   */
  getStatusOptions(): { value: string; label: string }[] {
    return [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
    ];
  }

  /**
   * Extract error message from API error response
   */
  private extractErrorMessageFromResponse(errorResponse: {
    success?: boolean;
    errors?: Array<{ msg?: string; message?: string; path?: string }>;
    message?: string;
    error?: string | { errorMessage?: string };
  }): string | null {
    if (
      errorResponse.errors &&
      Array.isArray(errorResponse.errors) &&
      errorResponse.errors.length > 0
    ) {
      const errorMessages = errorResponse.errors
        .map(err => err.msg || err.message || 'Validation error')
        .filter(Boolean);
      return errorMessages.join('; ');
    }

    if (
      errorResponse.error &&
      typeof errorResponse.error === 'object' &&
      errorResponse.error !== null
    ) {
      const errorObj = errorResponse.error as { errorMessage?: string };
      if (errorObj.errorMessage) {
        return errorObj.errorMessage;
      }
    }

    if (errorResponse.message) {
      return errorResponse.message;
    }
    if (errorResponse.error && typeof errorResponse.error === 'string') {
      return errorResponse.error;
    }
    return null;
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof ApiError) {
      if (error.response && typeof error.response === 'object') {
        const fromResponse = this.extractErrorMessageFromResponse(
          error.response as {
            success?: boolean;
            errors?: Array<{ msg?: string; message?: string; path?: string }>;
            message?: string;
            error?: string | { errorMessage?: string };
          }
        );
        if (fromResponse) {
          return fromResponse;
        }
      }
      return error.message;
    }
    return error instanceof Error ? error.message : 'An unexpected error occurred';
  }
}

// Export singleton instance
export const userService = new UserService();
export default userService;
